import React, { createContext, useState, useContext, useEffect, useCallback, ReactNode } from 'react';
import axios from 'axios';

// User interface
export interface User {
  id: number;
  username: string;
  first_name: string;
  last_name: string;
  phone?: string;
  is_admin: boolean;
  last_login?: string;
}

// Auth context interface
interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string;
  login: (
    username: string,
    password: string,
    rememberMe?: boolean,
  ) => Promise<{ success: boolean; message?: string; needsPhone?: boolean; pendingToken?: string; profile?: Partial<User> }>;
  loginWithGoogle: (
    credential: string
  ) => Promise<{ success: boolean; message?: string; needsPhone?: boolean; pendingToken?: string; profile?: Partial<User> }>;
  completePhone: (
    pendingToken: string,
    phone: string,
    firstName?: string,
    lastName?: string,
  ) => Promise<{ success: boolean; message?: string }>;
  signup: (userData: SignupData) => Promise<{ success: boolean; message?: string }>;
  forgotPassword: (email: string) => Promise<{ success: boolean; message?: string }>;
  resetPassword: (token: string, password: string) => Promise<{ success: boolean; message?: string }>;
  updateProfile: (data: UpdateProfileData) => Promise<{ success: boolean; message?: string }>;
  logout: () => void;
}

interface UpdateProfileData {
  first_name: string;
  last_name: string;
  phone: string;
  current_password?: string;
  new_password?: string;
}

// Signup data interface
interface SignupData {
  username: string;
  password: string;
  first_name: string;
  last_name: string;
  phone: string;
}

// Auth provider props
interface AuthProviderProps {
  children: ReactNode;
}

// --- Storage helpers ---
// Token/user are stored in localStorage (remember me) or sessionStorage (session only).

const storeAuth = (token: string, user: User, remember: boolean) => {
  const s = remember ? localStorage : sessionStorage;
  s.setItem('token', token);
  s.setItem('user', JSON.stringify(user));
};

const getActiveStorage = (): Storage =>
  localStorage.getItem('token') ? localStorage : sessionStorage;

const clearAuth = () => {
  localStorage.removeItem('token');
  localStorage.removeItem('user');
  sessionStorage.removeItem('token');
  sessionStorage.removeItem('user');
};

// Create auth context with default values
const AuthContext = createContext<AuthContextType>({
  user: null,
  isAuthenticated: false,
  isLoading: true,
  error: '',
  login: async () => ({ success: false }),
  loginWithGoogle: async () => ({ success: false }),
  completePhone: async () => ({ success: false }),
  signup: async () => ({ success: false }),
  forgotPassword: async () => ({ success: false }),
  resetPassword: async () => ({ success: false }),
  updateProfile: async () => ({ success: false }),
  logout: () => {},
});

// Custom hook to use auth context
export const useAuth = () => useContext(AuthContext);

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');
  // Tracks the remember-me choice across the phone-completion flow
  const rememberMeRef = React.useRef<boolean>(false);

  // Check if user is already logged in
  useEffect(() => {
    const checkAuth = () => {
      const s = getActiveStorage();
      const token = s.getItem('token');
      const userData = s.getItem('user');

      if (token && userData) {
        try {
          setUser(JSON.parse(userData));
          setIsAuthenticated(true);
          axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        } catch (err) {
          clearAuth();
        }
      }

      setIsLoading(false);
    };

    checkAuth();
  }, []);

  // Login function
  const login = async (
    username: string,
    password: string,
    rememberMe: boolean = false,
  ): Promise<{ success: boolean; message?: string; needsPhone?: boolean; pendingToken?: string; profile?: Partial<User> }> => {
    try {
      setError('');
      setIsLoading(true);
      rememberMeRef.current = rememberMe;

      const response = await axios.post('/api/auth/login', { username, password, remember_me: rememberMe });

      if (response.data && response.data.needs_phone) {
        return {
          success: false,
          needsPhone: true,
          pendingToken: response.data.pending_token,
          profile: response.data.profile ?? response.data.user,
        };
      }

      const { token, user } = response.data as { token: string; user: User };

      storeAuth(token, user, rememberMe);
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;

      setUser(user);
      setIsAuthenticated(true);

      return { success: true };
    } catch (err: any) {
      const message = err.response?.data?.error || 'Login failed';
      setError(message);
      return { success: false, message };
    } finally {
      setIsLoading(false);
    }
  };

  const loginWithGoogle = useCallback(async (
    credential: string
  ): Promise<{ success: boolean; message?: string; needsPhone?: boolean; pendingToken?: string; profile?: Partial<User> }> => {
    try {
      setError('');
      setIsLoading(true);

      const response = await axios.post('/api/auth/google', { credential });

      if (response.data && response.data.needs_phone) {
        return {
          success: false,
          needsPhone: true,
          pendingToken: response.data.pending_token,
          profile: response.data.profile ?? response.data.user,
        };
      }

      const { token, user } = response.data as { token: string; user: User };

      storeAuth(token, user, true);
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;

      setUser(user);
      setIsAuthenticated(true);

      return { success: true };
    } catch (err: any) {
      const message = err.response?.data?.error || 'Google login failed';
      setError(message);
      return { success: false, message };
    } finally {
      setIsLoading(false);
    }
  }, []);

  const completePhone = async (
    pendingToken: string,
    phone: string,
    firstName?: string,
    lastName?: string,
  ): Promise<{ success: boolean; message?: string }> => {
    try {
      setError('');
      setIsLoading(true);

      const response = await axios.post('/api/auth/complete-phone', {
        pending_token: pendingToken,
        phone,
        first_name: firstName,
        last_name: lastName,
      });

      const { token, user } = response.data as { token: string; user: User };

      storeAuth(token, user, rememberMeRef.current);
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;

      setUser(user);
      setIsAuthenticated(true);

      return { success: true };
    } catch (err: any) {
      const message = err.response?.data?.error || 'Failed to save phone';
      setError(message);
      return { success: false, message };
    } finally {
      setIsLoading(false);
    }
  };

  // Signup function
  const signup = async (userData: SignupData): Promise<{ success: boolean; message?: string }> => {
    try {
      setError('');
      setIsLoading(true);

      await axios.post('/api/auth/signup', userData);

      return { success: true };
    } catch (err: any) {
      const message = err.response?.data?.error || 'Signup failed';
      setError(message);
      return { success: false, message };
    } finally {
      setIsLoading(false);
    }
  };

  const forgotPassword = async (email: string): Promise<{ success: boolean; message?: string }> => {
    try {
      setError('');
      const response = await axios.post('/api/auth/forgot-password', { email });
      return { success: true, message: response.data.message };
    } catch (err: any) {
      const message = err.response?.data?.error || 'Failed to send reset email';
      return { success: false, message };
    }
  };

  const resetPassword = async (token: string, password: string): Promise<{ success: boolean; message?: string }> => {
    try {
      setError('');
      const response = await axios.post('/api/auth/reset-password', { token, password });
      return { success: true, message: response.data.message };
    } catch (err: any) {
      const message = err.response?.data?.error || 'Failed to reset password';
      return { success: false, message };
    }
  };

  const updateProfile = async (data: UpdateProfileData): Promise<{ success: boolean; message?: string }> => {
    try {
      setError('');
      const response = await axios.put('/api/user/profile', data);
      const updatedUser = response.data as User;
      setUser(updatedUser);
      getActiveStorage().setItem('user', JSON.stringify(updatedUser));
      return { success: true };
    } catch (err: any) {
      const message = err.response?.data?.error || 'Failed to update profile';
      return { success: false, message };
    }
  };

  // Logout function
  const logout = (): void => {
    clearAuth();
    delete axios.defaults.headers.common['Authorization'];

    setUser(null);
    setIsAuthenticated(false);
  };

  // Context value
  const value: AuthContextType = {
    user,
    isAuthenticated,
    isLoading,
    error,
    login,
    loginWithGoogle,
    completePhone,
    signup,
    forgotPassword,
    resetPassword,
    updateProfile,
    logout
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
